/* -----------------------------------------------------------------------------------------------------
  - Created: [31-01-2025] by [SREERAJ]
  - Modified: [YYYY-MM-DD] 
  - Description: This file contains Roles list and modals
  -------------------------------------------------------------------------------------------------------
*/
import React, { useEffect, useState } from 'react'
import './RoleTableStyle.css'
import { get_role_by_id, get_roles_with_pagination, role_creation, role_deletion, role_update } from '../../../../config/SettingsApis/rolesApis';
import CustomBottomPagination from '../../../../Components/custom_components/CustomBottomPagination/CustomBottomPagination'
import CustomTable from '../../../../Components/custom_components/CustomTable/CustomTable'
import RolesSearchFilter from '../RolesSearchFilter/RolesSearchFilter'
import CustomModal from '../../../../Components/custom_components/CustomModal/CustomModal'
import RoleForm from '../RoleForm/RoleForm'
import { ErrorToast, InfoToast, SuccessToast } from '../../../../utils/toast'
import CustomDeleteConfirmModal from '../../../../Components/custom_components/CustomDeleteConfirmModal/CustomDeleteConfirmModal'
import { handlePaginationEntries } from '../../../../utils/pagination'
import { useSelector } from 'react-redux'
import { hasPrivilege } from '../../../../utils/privileges'
import Loader from '../../../../Components/Loader/Loader'
import { Icons } from '../../../../utils/GlobalIcons'
import CustomTooltipWithoutRoundBorder from '../../../../Components/CustomActionButtonWithTooltip/CustomTooltipWithoutRoundBorder'
import { getStatusColors } from '../../../../utils/constants'

function RoleTable() {
    const privileges = useSelector((state) => state.privileges.privileges);

    const [rolesListing, setRolesListing] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);  // Current page number
    const [totalItemPerPage, setTotalItemPerPage] = useState(25);  // Items per page
    const [totalPages, setTotalPages] = useState(0);  // Total number of pages
    const [refresh, setRefresh] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    //  variables for both modals
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState({
        isOpen: false,
        id: '',
    });
    const [modalProps, setModalProps] = useState({
        isOpen: false,
        id: '',
        formValues: {}
    });

    // Pagination state
    const [pagination, setPagination] = useState({
        start: 0,
        end: 0,
        totalEntries: 0,
    });
    const [isLoading, setIsLoading] = useState(false); // Loader state

    useEffect(() => {
        get_roles()
    }, [pageNumber, totalItemPerPage, refresh])


    //get roles list with pagination
    const get_roles = async () => {
        const formData = new FormData();
        formData.append('page', pageNumber);
        formData.append('pageItemcount', totalItemPerPage);

        try {
            setIsLoading(true)
            const response = await get_roles_with_pagination(formData)
            if (response?.status === "success") {
                setRolesListing(response.data.data)
                setTotalPages(response.data.last_page)
                const updatedPagination = handlePaginationEntries(response.data.from, response.data.to, response.data.total)
                setPagination(updatedPagination);
            } else {

            }
        } catch (error) {
            setRolesListing([])
        } finally {
            setIsLoading(false)
        }
    }



    // Define the required permission actions
    const PERMISSION_ACTIONS = {
        view: 4,
        edit: 5,
        history: 6,
        add: 3,
    };

    // Map permissions to boolean values based on privileges
    const userPermissions = {
        view: hasPrivilege(privileges, "Settings", 10, PERMISSION_ACTIONS.view),
        edit: hasPrivilege(privileges, "Settings", 10, PERMISSION_ACTIONS.edit),
        history: hasPrivilege(privileges, "Settings", 10, PERMISSION_ACTIONS.history),
        add: hasPrivilege(privileges, "Settings", 10, PERMISSION_ACTIONS.add),
    };

    // / Function to check if any action-related permissions are granted
    const hasActionPermissions = userPermissions.view || userPermissions.edit || userPermissions.history;


    // FOR TABLE COLUMNS
    const columns = [
        {
            title: "#",
            key: "key",
        },
        {
            title: "Role",
            key: "role",
        },
        {
            title: "Description",
            key: "RolesDescription",
        },
       {
            title: "Status",
            key: "status",
            render: (_, record) => {
                //  actual status text
                const statusText = record?.status || "Unknown";

                const statusCode = statusText.toLowerCase() === "active" ? 1 : statusText.toLowerCase() === "inactive" ? 2 : '';
                const style = getStatusColors(statusCode);

                return (
                    <span className="status-badge" style={style}>
                        {statusText}
                    </span>
                );
            },
        },
        { title: "Actions", key: "actions", render: (text, row) => renderActions(row) },
    ].filter(column => column.key !== "actions" || hasActionPermissions); //removes the "Actions" column when hasActionPermissions is false


    // Render custom actions based on user role
    const renderActions = (row) => {
        return (
            <div className='action-container'>
                {userPermissions.edit &&
                    <CustomTooltipWithoutRoundBorder title="Edit">
                        <Icons.Edit className="edit-action-icon" onClick={() => handleModal(true, row?.id)} />
                    </CustomTooltipWithoutRoundBorder>
                }
                {/* <FaTrashAlt className="delete-action-icon" onClick={() => handlDeleteModal(true, row?.id)} /> */}
            </div>

        );
    };

    // FOR TABLE ITEMS
    const datas = () => rolesListing?.map((item, index) => ({
        key: pageNumber * totalItemPerPage - (totalItemPerPage - (index + 1)),
        id: item.RoleID,
        role: item.Rolename,
        status: item?.statusName,
        statusId: item.Rolestatus,
        RolesDescription: item.RolesDescription
    }));


    //FOR HANDLE ADD/EDIT MODAL OPEN
    const handleModal = async (isOpen, role_Id) => {

        if (role_Id) { //FOR EDIT
            const params = {
                roleId: role_Id
            }
            GetRoleById(params)
        } else { //FOR ADD NEW ROLE
            setModalProps({
                isOpen,
                id: '',
                formValues:
                {
                    RoleName: '',
                    Rolestatus: 1,
                    RoleDescription: ''
                }
            })
        }



    };

    //FOR GET ROLE DETAILS BY ID
    const GetRoleById = async (params) => {
        try {
            const response = await get_role_by_id(params)
            if (response?.status === "success") {
                setModalProps({
                    isOpen: true,
                    id: response?.data?.RoleID,
                    formValues: //WHICH MENAS EDIT IS ACTIVE
                    {
                        RoleName: response?.data?.RoleName,
                        Rolestatus: response?.data?.Rolestatus,
                        RoleDescription: response?.data?.RoleDescription || "",
                    }
                });
            } else {
                ErrorToast(response?.message || `Something Went Wrong!`)
            }
        } catch (error) {
            ErrorToast(`Something Went Wrong!`)
        }
    }

    // Function to handle HIDE/SHOW DELETE MODAL
    const handlDeleteModal = (isOpen, id = '') => {
        setIsDeleteModalOpen({ isOpen, id })
    }
    //FOR ADD/UPDATE ROLE FUNCTION
    const handleRoleSave = async (values, roleId) => {
        const formData = new FormData();
        for (const key in values) {
            formData.append(key, values[key]);
        }

        try {
            setIsButtonDisabled(true);

            // Append roleId if updating
            if (roleId) {
                formData.append('roleId', roleId);
            }

            // Choose the appropriate API function based on roleId
            const apiFunction = roleId ? role_update : role_creation;
            const response = await apiFunction(formData);

            // Handle response
            const message = response?.data?.message || 'Something went wrong!';
            const status = response?.data?.status;

            if (status === "success") {
                SuccessToast(message);
                handleModal(false);
                setRefresh(!refresh);
            } else {
                ErrorToast(message);
            }
        } catch (error) {
            let errorMessage = 'Something went wrong!';
            // Check if error response contains dynamic errors
            if (error?.data?.errors) {
                for (const field in error.data.errors) {
                    if (error.data.errors[field] && error.data.errors[field].length > 0) {
                        errorMessage = error.data.errors[field][0];
                        break;  // want to stop after the first error field
                    }
                }
            }

            ErrorToast(errorMessage);
        } finally {
            setIsButtonDisabled(false);
        }
    };

    // FOR ROLE DELETION
    const handleDeleteRole = async () => {
        const params = {
            roleId: isDeleteModalOpen?.id
        }
        try {
            const response = await role_deletion(params)

            if (response?.data?.status === "success") {
                SuccessToast(response?.data?.message)
                handlDeleteModal(false, '')
                setRefresh(!refresh)
            } else {
                ErrorToast(response?.data?.message || `Something Went Wrong!`)
            }
        } catch (error) {
            ErrorToast(`Something Went Wrong!`)
        }
    }

    // Update pagination
    const updatePagination = (pageNumber) => setPageNumber(pageNumber);
    const updateItemPageCount = (pageItemCount) => setTotalItemPerPage(pageItemCount);

    return (
        <div className="role-table-container">
            <RolesSearchFilter tableTitle="Roles " isAddEdiModalOpen={modalProps.isOpen} handleAddEditModal={handleModal} isAddPermission={userPermissions?.add} />
            <CustomTable columns={columns} rows={isLoading ? [] : datas()} isLoading={isLoading} />

            {/* Pagination component */}
            {isLoading ? <Loader /> : <CustomBottomPagination
                items_per_page={totalItemPerPage} // Total number of entries
                current_page={pageNumber}   // The current active page
                total_page_count={totalPages}  // Total number of pages
                on_change_value={updatePagination} // Callback for page change
                on_page_count_update={updateItemPageCount}   // Callback for items per page change
                pagination={pagination} //FOR PAGINATION ENTRIES

            />}

            {/* Role Add/Edit Modal */}
            {modalProps.isOpen && <CustomModal
                open={modalProps.isOpen}
                handleClose={() => handleModal(false)}
                title={modalProps.id !== '' ? "Edit Role" : "Create Role"}
                body={<RoleForm
                    isButtonDisabled={isButtonDisabled}
                    handleRoleSave={handleRoleSave}
                    id={modalProps.id}
                    formIntialValues={modalProps.formValues}
                />}
                maxWidth="1000px"
            />}


            {/* Role Delete Modal */}
            {isDeleteModalOpen?.isOpen && <CustomDeleteConfirmModal
                module_name={'Role'}
                open_modal={isDeleteModalOpen?.isOpen}
                close_modal={handlDeleteModal}
                confirm_function={handleDeleteRole}
            />}
        </div>
    );
}

export default RoleTable;
